import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ImportWarningsBanner } from "../components/ImportWarningsBanner";
import { TemplateHistoryPanel } from "../components/TemplateHistoryPanel";
import { Button } from "../components/ui/button";
import { ApiError } from "../lib/api";
import type { MigrationWarning } from "../lib/migrations";
import { uploadAssetUrl } from "../lib/assets";
import { getDefaultBrandKit } from "../lib/brandKits";
import {
  getTemplate,
  putTemplate,
  type FullTemplate,
} from "../lib/templates";
import { useAuthStore } from "../stores/auth";

// Design tokens + editor styles (inert until restyle Phase B migrates
// components onto eb-* classes). Static import is fine: it's a few KB
// of CSS custom properties, not worth a separate chunk. Fonts come from
// the tokens package directly — the host bundler emits the woff2 as
// lazy assets (the editor's lib build would inline them as base64).
import "@email-builder/design-tokens/fonts.css";
import "@email-builder/editor-react/style.css";

// Lazy-load the editor — TipTap + @dnd-kit + ProseMirror weigh ~170KB
// gzipped, the Templates list shouldn't pay that cost.
const EmailBuilder = lazy(() =>
  import("@email-builder/editor-react").then((m) => ({ default: m.EmailBuilder })),
);

export function EditorPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.user?.tenant_id ?? "");
  const sessionToken = useAuthStore((s) => s.token ?? "");
  const userEmail = useAuthStore((s) => s.user?.email ?? "");

  // Set by the Import-HTML flow (Migration 2.2 §7): a per-code breakdown of
  // parser + re-host warnings, passed via router state so it shows once.
  // history.state DOES survive a page reload, so dismissing must CLEAR it
  // (dismissImportWarnings) — otherwise the banner reappears on every reload.
  const location = useLocation();
  const importWarnings =
    (location.state as { importWarnings?: MigrationWarning[] } | null)
      ?.importWarnings ?? [];
  const dismissImportWarnings = (): void => {
    const next = { ...((location.state as Record<string, unknown> | null) ?? {}) };
    delete next.importWarnings;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: Object.keys(next).length > 0 ? next : null,
    });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["template", id],
    queryFn: () => getTemplate(id),
    enabled: id !== "",
  });

  // Brand kit is optional context for the editor — fetch in parallel
  // with the template, don't block render on it.
  const { data: brandKit } = useQuery({
    queryKey: ["brand-kit", "default"],
    queryFn: getDefaultBrandKit,
    staleTime: 5 * 60 * 1000,
  });

  // Local working copy — the editor is controlled, so we hold the
  // current document here and feed it back into the editor on each
  // change. PUT goes through the save mutation.
  const [working, setWorking] = useState<FullTemplate | null>(null);
  // Re-seed the working copy when the template id changes, not just on the
  // first load. EditorPage stays mounted across /templates/:id/edit param
  // changes, so a plain `working === null` guard would keep showing the
  // first template after navigating to a second one. `data` is keyed by id
  // (TanStack Query) so it always matches the current route once loaded.
  const loadedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (data !== undefined && loadedIdRef.current !== id) {
      loadedIdRef.current = id;
      setWorking(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, id]);

  const saveMutation = useMutation({
    mutationFn: (doc: FullTemplate) => putTemplate(id, doc),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const handleSave = useCallback(
    async (doc: FullTemplate): Promise<void> => {
      try {
        await saveMutation.mutateAsync(doc);
      } catch (err) {
        // Re-throw with a richer message when the API returned RFC 7807
        // VALIDATION_FAILED — surface the FIRST field-level error so
        // the editor's toast actually tells the user where to look.
        if (err instanceof ApiError && err.code === "VALIDATION_FAILED") {
          // RFC 7807 errors from Laravel ApiException::validationFailed
          // are flattened at the top level of the problem doc, not
          // under extra. Look in both spots so we keep working if the
          // server format changes later.
          const direct = (err.problem as { errors?: Array<{ field?: string; message?: string }> }).errors;
          const nested = (err.problem.extra as { errors?: Array<{ field?: string; message?: string }> } | undefined)?.errors;
          const list = direct ?? nested;
          const first = list?.[0];
          if (first !== undefined) {
            // eslint-disable-next-line no-console
            console.error("Validation failed — full error list:", list);
            const where = first.field ?? "?";
            const what = first.message ?? "invalid value";
            throw new Error(`Schema validation: ${where} — ${what}`);
          }
        }
        throw err;
      }
    },
    [saveMutation],
  );

  if (id === "") {
    return <NotFound onBack={() => navigate("/templates")} />;
  }
  if (isLoading) {
    return <FullPage>Loading template…</FullPage>;
  }
  if (error instanceof ApiError) {
    return (
      <FullPage>
        <p
          role="alert"
          className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
        >
          Couldn&apos;t load template: {error.problem.detail || error.problem.title}
        </p>
        <Link to="/templates" className="mt-4 inline-block text-sm text-primary-600 hover:underline">
          Back to templates
        </Link>
      </FullPage>
    );
  }
  if (working === null) {
    return <FullPage>Loading template…</FullPage>;
  }

  return (
    <div className="flex h-screen w-screen flex-col">
      {/* No separate portal header row — the editor top bar hosts the
          back-nav, save status and History (see onBack / headerActions). */}
      {importWarnings.length > 0 && (
        <ImportWarningsBanner
          warnings={importWarnings}
          onDismiss={dismissImportWarnings}
        />
      )}
      <div className="flex-1 overflow-hidden">
        <Suspense fallback={<FullPage>Loading editor…</FullPage>}>
          <EmailBuilder
            // The editor consumes the canonical EmailTemplate type from
            // shared-types; portal carries it as opaque FullTemplate.
            // Cast at the bridge — this file is the only place that
            // shoulders the dependency.
            template={working as never}
            onChange={(t) => setWorking(t as unknown as FullTemplate)}
            onSave={handleSave as never}
            // Editor top bar hosts these so the portal needs no header row.
            onBack={() => navigate("/templates")}
            headerActions={
              <TemplateHistoryPanel
                templateId={id}
                onRestored={(restored) => setWorking(restored as FullTemplate)}
              />
            }
            autoSave={{ debounceMs: 3000 }}
            ai={
              tenantId !== "" && sessionToken !== ""
                ? {
                    tenantId,
                    // Bearer for /v1/ai/generate is the user's own
                    // Sanctum session token. Laravel pulls tenant_id
                    // out of the auth context (overriding any value
                    // the editor might send in the body) and forwards
                    // to the internal ai-service with its shared
                    // service-to-service secret.
                    token: sessionToken,
                  }
                : { disabled: true }
            }
            hostAdapter={{ uploadAsset: uploadAssetUrl }}
            brandKit={brandKit ?? null}
            user={userEmail !== "" ? { email: userEmail } : undefined}
            modules={
              sessionToken !== ""
                ? { token: sessionToken, templateId: id }
                : { disabled: true }
            }
          />
        </Suspense>
      </div>
    </div>
  );
}

function FullPage({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <main className="mx-auto flex min-h-full max-w-3xl flex-col items-center justify-center gap-4 px-6 py-12 text-center text-sm text-neutral-600">
      {children}
    </main>
  );
}

function NotFound({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <FullPage>
      <p>Template not found.</p>
      <Button variant="primary" onClick={onBack}>
        Back to templates
      </Button>
    </FullPage>
  );
}

